import { create } from 'zustand'
import type { VideoVisibility } from '../types/database.types'

export type VideoSortBy = 'newest' | 'oldest' | 'popular' | 'title'

export interface VideoFilters {
  search: string
  category: string | null
  tags: string[]
  sortBy: VideoSortBy
  visibility: VideoVisibility | 'all'
}

export interface Pagination {
  page: number
  pageSize: number
  total: number
}

interface VideoState {
  // Filters
  filters: VideoFilters
  setFilter: <K extends keyof VideoFilters>(key: K, value: VideoFilters[K]) => void
  setFilters: (filters: Partial<VideoFilters>) => void
  resetFilters: () => void

  // Pagination
  pagination: Pagination
  setPage: (page: number) => void
  setPageSize: (size: number) => void
  setTotal: (total: number) => void
  resetPagination: () => void

  // Selected video (for modals, detail view, etc.)
  selectedVideoId: string | null
  setSelectedVideo: (id: string | null) => void

  // Upload state
  uploadProgress: number
  isUploading: boolean
  setUploadProgress: (progress: number) => void
  setIsUploading: (uploading: boolean) => void
  resetUploadState: () => void
}

const defaultFilters: VideoFilters = {
  search: '',
  category: null,
  tags: [],
  sortBy: 'newest',
  visibility: 'all',
}

const defaultPagination: Pagination = {
  page: 1,
  pageSize: 12,
  total: 0,
}

export const useVideoStore = create<VideoState>((set) => ({
  // Filters
  filters: defaultFilters,

  setFilter: (key, value) =>
    set((state) => ({
      filters: { ...state.filters, [key]: value },
      // Reset to page 1 when filters change
      pagination: { ...state.pagination, page: 1 },
    })),

  setFilters: (filters) =>
    set((state) => ({
      filters: { ...state.filters, ...filters },
      pagination: { ...state.pagination, page: 1 },
    })),

  resetFilters: () =>
    set({
      filters: defaultFilters,
      pagination: defaultPagination,
    }),

  // Pagination
  pagination: defaultPagination,

  setPage: (page) =>
    set((state) => ({
      pagination: { ...state.pagination, page },
    })),

  setPageSize: (pageSize) =>
    set((state) => ({
      pagination: { ...state.pagination, pageSize, page: 1 },
    })),

  setTotal: (total) =>
    set((state) => ({
      pagination: { ...state.pagination, total },
    })),

  resetPagination: () =>
    set({ pagination: defaultPagination }),

  // Selected video
  selectedVideoId: null,

  setSelectedVideo: (id) => set({ selectedVideoId: id }),

  // Upload state
  uploadProgress: 0,
  isUploading: false,

  setUploadProgress: (uploadProgress) => set({ uploadProgress }),

  setIsUploading: (isUploading) => set({ isUploading }),

  resetUploadState: () =>
    set({
      uploadProgress: 0,
      isUploading: false,
    }),
}))

// Helper to build query params from filters
export const buildVideoQueryParams = (filters: VideoFilters, pagination: Pagination) => {
  const params = new URLSearchParams()

  if (filters.search) params.set('q', filters.search)
  if (filters.category) params.set('category', filters.category)
  if (filters.tags.length > 0) params.set('tags', filters.tags.join(','))
  if (filters.sortBy !== 'newest') params.set('sort', filters.sortBy)
  if (filters.visibility !== 'all') params.set('visibility', filters.visibility)

  params.set('page', pagination.page.toString())
  params.set('limit', pagination.pageSize.toString())

  return params
}
